import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  Modal,
  TextInput,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Keyboard,
  TouchableWithoutFeedback,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';
import {
  deathVerificationService,
  SecretCodeRecipient,
  RecipientRelationship,
  RELATIONSHIP_LABELS,
  CreateSecretCodeRecipientDto,
} from '../../services/deathVerificationService';

// Import icons
import backIcon from '../../assets/icons/back.png';
import addIcon from '../../assets/icons/add.png';
import editIcon from '../../assets/icons/edit.png';
import trashIcon from '../../assets/icons/trash.png';
import checkIcon from '../../assets/icons/check.png';
import phoneIcon from '../../assets/icons/phone.png';
import userIcon from '../../assets/icons/user.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('SecretCodeRecipients');

interface SecretCodeRecipientsScreenProps {
  navigation: NavigationProp<any>;
}

const SecretCodeRecipientsScreen: React.FC<SecretCodeRecipientsScreenProps> = ({ navigation }) => {
  const [recipients, setRecipients] = useState<SecretCodeRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<SecretCodeRecipient | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRelationship, setFormRelationship] = useState<RecipientRelationship>(RecipientRelationship.OTHER);
  const [showRelationshipOptions, setShowRelationshipOptions] = useState(false);

  // Input refs for keyboard navigation
  const phoneInputRef = useRef<TextInput>(null);
  const emailInputRef = useRef<TextInput>(null);

  useFocusEffect(
    useCallback(() => {
      loadRecipients();
    }, [])
  );

  const loadRecipients = async () => {
    try {
      setLoading(true);
      const data = await deathVerificationService.getRecipients();
      // Sort by priority
      const sorted = data.sort((a, b) => a.priority - b.priority);
      setRecipients(sorted);
    } catch (error) {
      logger.error('Error loading recipients:', error);
      Alert.alert('Error', 'Failed to load secret code recipients');
    } finally {
      setLoading(false);
    }
  };

  // Format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;
    let formatted = '';
    if (match[1]) {
      formatted = `(${match[1]}`;
    }
    if (match[2]) {
      formatted += match[2].length === 3 ? `) ${match[2]}` : match[2];
    }
    if (match[3]) {
      formatted += match[3] ? `-${match[3]}` : '';
    }
    return formatted;
  };

  const handlePhoneChange = (value: string) => {
    setFormPhone(formatPhoneNumber(value));
  };

  const resetForm = () => {
    setFormName('');
    setFormPhone('');
    setFormEmail('');
    setFormRelationship(RecipientRelationship.OTHER);
    setShowRelationshipOptions(false);
  };

  const openAddModal = () => {
    if (recipients.filter(r => r.isActive).length >= 3) {
      Alert.alert('Limit Reached', 'You can only have up to 3 secret code recipients.');
      return;
    }
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (recipient: SecretCodeRecipient) => {
    setEditingRecipient(recipient);
    setFormName(recipient.recipientName);
    setFormPhone(formatPhoneNumber(recipient.phoneNumber));
    setFormEmail(recipient.email || '');
    setFormRelationship(recipient.relationship);
    setShowEditModal(true);
  };

  const handleAddRecipient = async () => {
    if (!formName.trim() || !formPhone.trim() || !formEmail.trim()) {
      Alert.alert('Required Fields', 'Please enter a name, phone number, and email address.');
      return;
    }

    try {
      setSaving(true);
      const activeCount = recipients.filter(r => r.isActive).length;
      const newPriority = activeCount + 1;

      const data: CreateSecretCodeRecipientDto = {
        priority: newPriority,
        recipientName: formName.trim(),
        phoneNumber: formPhone.trim(),
        relationship: formRelationship,
        email: formEmail.trim() || undefined,
      };

      await deathVerificationService.createRecipient(data);
      setShowAddModal(false);
      resetForm();
      await loadRecipients();
      Alert.alert('Success', 'Recipient added successfully');
    } catch (error: any) {
      logger.error('Error adding recipient:', error);
      Alert.alert('Error', error.message || 'Failed to add recipient');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRecipient = async () => {
    if (!editingRecipient) return;

    if (!formName.trim() || !formPhone.trim() || !formEmail.trim()) {
      Alert.alert('Required Fields', 'Please enter a name, phone number, and email address.');
      return;
    }

    try {
      setSaving(true);
      await deathVerificationService.updateRecipient(editingRecipient.id, {
        recipientName: formName.trim(),
        phoneNumber: formPhone.trim(),
        relationship: formRelationship,
        email: formEmail.trim() || undefined,
      });
      setShowEditModal(false);
      setEditingRecipient(null);
      resetForm();
      await loadRecipients();
      Alert.alert('Success', 'Recipient updated successfully');
    } catch (error: any) {
      logger.error('Error updating recipient:', error);
      Alert.alert('Error', error.message || 'Failed to update recipient');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRecipient = (recipient: SecretCodeRecipient) => {
    Alert.alert(
      'Delete Recipient',
      `Are you sure you want to remove ${recipient.recipientName} from your secret code recipients?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await deathVerificationService.deleteRecipient(recipient.id);
              await loadRecipients();
              Alert.alert('Success', 'Recipient removed');
            } catch (error: any) {
              logger.error('Error deleting recipient:', error);
              Alert.alert('Error', error.message || 'Failed to delete recipient');
            }
          },
        },
      ]
    );
  };

  const handleVerifyRecipient = async (recipient: SecretCodeRecipient) => {
    try {
      await deathVerificationService.verifyRecipient(recipient.id);
      await loadRecipients();
      Alert.alert('Verified', `${recipient.recipientName}'s contact information has been marked as verified.`);
    } catch (error: any) {
      logger.error('Error verifying recipient:', error);
      Alert.alert('Error', error.message || 'Failed to verify recipient');
    }
  };

  const getPriorityLabel = (priority: number): string => {
    switch (priority) {
      case 1:
        return 'Primary';
      case 2:
        return 'First Backup';
      case 3:
        return 'Second Backup';
      default:
        return `Priority ${priority}`;
    }
  };

  const formatLastVerified = (dateStr: string | null): string => {
    if (!dateStr) return 'Never verified';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Verified today';
    if (diffDays === 1) return 'Verified yesterday';
    if (diffDays < 30) return `Verified ${diffDays} days ago`;
    if (diffDays < 180) return `Verified ${Math.floor(diffDays / 30)} months ago`;
    return 'Needs re-verification';
  };

  const needsVerification = (dateStr: string | null): boolean => {
    if (!dateStr) return true;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays > 180; // More than 6 months
  };

  const renderRecipientCard = (recipient: SecretCodeRecipient) => (
    <View key={recipient.id} style={styles.recipientCard}>
      <View style={styles.recipientHeader}>
        <View style={styles.priorityBadge}>
          <Text style={styles.priorityText}>{getPriorityLabel(recipient.priority)}</Text>
        </View>
        {needsVerification(recipient.lastVerifiedAt) && (
          <View style={styles.warningBadge}>
            <Text style={styles.warningText}>Needs Verification</Text>
          </View>
        )}
      </View>

      <View style={styles.recipientInfo}>
        <View style={styles.infoRow}>
          <Image source={userIcon} style={styles.infoIcon} />
          <View>
            <Text style={styles.recipientName}>{recipient.recipientName}</Text>
            <Text style={styles.relationshipText}>
              {RELATIONSHIP_LABELS[recipient.relationship]}
            </Text>
          </View>
        </View>

        <View style={styles.infoRow}>
          <Image source={phoneIcon} style={styles.infoIcon} />
          <Text style={styles.phoneText}>{recipient.phoneNumber}</Text>
        </View>

        {recipient.email && (
          <Text style={styles.emailText}>{recipient.email}</Text>
        )}

        <Text style={styles.verifiedText}>
          {formatLastVerified(recipient.lastVerifiedAt)}
        </Text>
      </View>

      <View style={styles.recipientActions}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => handleVerifyRecipient(recipient)}
        >
          <Image source={checkIcon} style={[styles.actionIcon, { tintColor: '#27AE60' }]} />
          <Text style={[styles.actionText, { color: '#27AE60' }]}>Verify</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => openEditModal(recipient)}
        >
          <Image source={editIcon} style={[styles.actionIcon, { tintColor: '#29BFC0' }]} />
          <Text style={[styles.actionText, { color: '#29BFC0' }]}>Edit</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => handleDeleteRecipient(recipient)}
        >
          <Image source={trashIcon} style={[styles.actionIcon, { tintColor: '#E85C5C' }]} />
          <Text style={[styles.actionText, { color: '#E85C5C' }]}>Remove</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderFormModal = (isEdit: boolean) => (
    <Modal
      visible={isEdit ? showEditModal : showAddModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => {
        Keyboard.dismiss();
        if (isEdit) {
          setShowEditModal(false);
          setEditingRecipient(null);
        } else {
          setShowAddModal(false);
        }
        resetForm();
      }}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.modalOverlay}
      >
        <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ScrollView
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                <Text style={styles.modalTitle}>
                  {isEdit ? 'Edit Recipient' : 'Add Secret Code Recipient'}
                </Text>

                <Text style={styles.inputLabel}>Name *</Text>
                <TextInput
                  style={styles.textInput}
                  value={formName}
                  onChangeText={setFormName}
                  placeholder="Enter name"
                  placeholderTextColor="#999"
                  returnKeyType="next"
                  onSubmitEditing={() => phoneInputRef.current?.focus()}
                  blurOnSubmit={false}
                />

                <Text style={styles.inputLabel}>Phone Number *</Text>
                <TextInput
                  ref={phoneInputRef}
                  style={styles.textInput}
                  value={formPhone}
                  onChangeText={handlePhoneChange}
                  placeholder="(555) 555-5555"
                  placeholderTextColor="#999"
                  keyboardType="phone-pad"
                  returnKeyType="next"
                  onSubmitEditing={() => emailInputRef.current?.focus()}
                  blurOnSubmit={false}
                />

                <Text style={styles.inputLabel}>Email *</Text>
                <TextInput
                  ref={emailInputRef}
                  style={styles.textInput}
                  value={formEmail}
                  onChangeText={setFormEmail}
                  placeholder="Enter email"
                  placeholderTextColor="#999"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  returnKeyType="done"
                  onSubmitEditing={() => Keyboard.dismiss()}
                />

                <Text style={styles.inputLabel}>Relationship</Text>
                <TouchableOpacity
                  style={styles.dropdownButton}
                  onPress={() => {
                    Keyboard.dismiss();
                    setShowRelationshipOptions(!showRelationshipOptions);
                  }}
                >
                  <Text style={styles.dropdownButtonText}>
                    {RELATIONSHIP_LABELS[formRelationship]}
                  </Text>
                  <Image
                    source={require('../../assets/icons/chevron.png')}
                    style={[
                      styles.dropdownChevron,
                      showRelationshipOptions && { transform: [{ rotate: '270deg' }] }
                    ]}
                  />
                </TouchableOpacity>

                {showRelationshipOptions && (
                  <View style={styles.relationshipOptions}>
                    {Object.entries(RELATIONSHIP_LABELS).map(([key, label]) => {
                      const isSelected = formRelationship === key;
                      return (
                        <TouchableOpacity
                          key={key}
                          style={[styles.relationshipOption, isSelected && styles.relationshipOptionSelected]}
                          onPress={() => {
                            setFormRelationship(key as RecipientRelationship);
                            setShowRelationshipOptions(false);
                          }}
                        >
                          <Text style={[styles.relationshipOptionText, isSelected && styles.relationshipOptionTextSelected]}>
                            {label}
                          </Text>
                          {isSelected && (
                            <Text style={styles.relationshipCheckmark}>✓</Text>
                          )}
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}

                <View style={styles.modalButtons}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.cancelButton]}
                    onPress={() => {
                      Keyboard.dismiss();
                      if (isEdit) {
                        setShowEditModal(false);
                        setEditingRecipient(null);
                      } else {
                        setShowAddModal(false);
                      }
                      resetForm();
                    }}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.modalButton, styles.saveButton]}
                    onPress={isEdit ? handleUpdateRecipient : handleAddRecipient}
                    disabled={saving}
                  >
                    {saving ? (
                      <ActivityIndicator color="#FFF" size="small" />
                    ) : (
                      <Text style={styles.saveButtonText}>{isEdit ? 'Save' : 'Add'}</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </ScrollView>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </KeyboardAvoidingView>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Secret Code Recipients</Text>
        <TouchableOpacity onPress={openAddModal}>
          <Image source={addIcon} style={[styles.headerIcon, { tintColor: '#29BFC0' }]} />
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.infoCard}>
          <Text style={styles.infoTitle}>What are Secret Code Recipients?</Text>
          <Text style={styles.infoDescription}>
            When your successor submits a death certificate, we'll send a secret code via SMS to your
            designated recipients. Your successor must obtain this code and enter it to verify the request.
            {'\n\n'}
            We recommend adding multiple recipients (up to 3) in case one is unavailable.
            We'll contact them in priority order until someone responds.
          </Text>
        </View>

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading recipients...</Text>
          </View>
        ) : recipients.filter(r => r.isActive).length === 0 ? (
          <View style={styles.emptyContainer}>
            <Image source={userIcon} style={styles.emptyIcon} />
            <Text style={styles.emptyTitle}>No Recipients Added</Text>
            <Text style={styles.emptyDescription}>
              Add at least one trusted person who can receive the secret code when death verification
              is requested.
            </Text>
            <TouchableOpacity style={styles.addButton} onPress={openAddModal}>
              <Text style={styles.addButtonText}>Add Your First Recipient</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {recipients.filter(r => r.isActive).map(renderRecipientCard)}

            {recipients.filter(r => r.isActive).length < 3 && (
              <TouchableOpacity style={styles.addMoreButton} onPress={openAddModal}>
                <Image source={addIcon} style={styles.addMoreIcon} />
                <Text style={styles.addMoreText}>Add Another Recipient</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </ScrollView>

      {renderFormModal(false)}
      {renderFormModal(true)}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(30),
  },
  infoCard: {
    backgroundColor: '#E8F8F8',
    borderRadius: ms(12),
    padding: ms(16),
    marginBottom: ms(20),
    borderLeftWidth: ms(4),
    borderLeftColor: '#29BFC0',
  },
  infoTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(8),
  },
  infoDescription: {
    fontSize: ms(14, 0.3),
    color: '#555',
    lineHeight: ms(20, 0.3),
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: ms(40),
  },
  loadingText: {
    marginTop: ms(12),
    fontSize: ms(16, 0.3),
    color: '#666',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: ms(40),
    paddingHorizontal: ms(20),
  },
  emptyIcon: {
    width: s(64),
    height: s(64),
    tintColor: '#CCC',
    marginBottom: ms(16),
  },
  emptyTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(8),
  },
  emptyDescription: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(24),
    lineHeight: ms(20, 0.3),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(14),
    paddingHorizontal: ms(24),
    borderRadius: ms(25),
  },
  addButtonText: {
    color: '#FFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  recipientCard: {
    backgroundColor: '#FFF',
    borderRadius: ms(12),
    padding: ms(16),
    marginBottom: ms(16),
    borderWidth: ms(1),
    borderColor: '#E0E0E0',
  },
  recipientHeader: {
    flexDirection: 'row',
    marginBottom: ms(12),
    gap: ms(8),
  },
  priorityBadge: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(4),
    paddingHorizontal: ms(12),
    borderRadius: ms(12),
  },
  priorityText: {
    color: '#FFF',
    fontSize: ms(12, 0.3),
    fontWeight: '600',
  },
  warningBadge: {
    backgroundColor: '#FFF3CD',
    paddingVertical: ms(4),
    paddingHorizontal: ms(12),
    borderRadius: ms(12),
  },
  warningText: {
    color: '#856404',
    fontSize: ms(12, 0.3),
    fontWeight: '600',
  },
  recipientInfo: {
    marginBottom: ms(12),
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(8),
  },
  infoIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#666',
    marginRight: ms(12),
  },
  recipientName: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
  },
  relationshipText: {
    fontSize: ms(13, 0.3),
    color: '#666',
  },
  phoneText: {
    fontSize: ms(15, 0.3),
    color: '#333',
  },
  emailText: {
    fontSize: ms(13, 0.3),
    color: '#666',
    marginLeft: ms(32),
    marginBottom: ms(8),
  },
  verifiedText: {
    fontSize: ms(13, 0.3),
    color: '#888',
    marginTop: ms(4),
  },
  recipientActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: ms(16),
    borderTopWidth: ms(1),
    borderTopColor: '#F0F0F0',
    paddingTop: ms(12),
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(4),
  },
  actionIcon: {
    width: s(18),
    height: s(18),
  },
  actionText: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
  },
  addMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF',
    borderRadius: ms(12),
    padding: ms(16),
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    borderStyle: 'dashed',
    gap: ms(8),
  },
  addMoreIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#29BFC0',
  },
  addMoreText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    padding: ms(20),
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(20),
    textAlign: 'center',
  },
  inputLabel: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(8),
  },
  textInput: {
    backgroundColor: '#F5F5F5',
    borderRadius: ms(8),
    padding: ms(14),
    fontSize: ms(16, 0.3),
    color: '#333',
    marginBottom: ms(16),
    borderWidth: ms(1),
    borderColor: '#E0E0E0',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F5F5F5',
    borderRadius: ms(8),
    padding: ms(14),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E0E0E0',
  },
  dropdownButtonText: {
    fontSize: ms(16, 0.3),
    color: '#333',
  },
  dropdownChevron: {
    width: s(14),
    height: s(14),
    tintColor: '#29BFC0',
    transform: [{ rotate: '90deg' }],
  },
  relationshipOptions: {
    backgroundColor: '#F8F8F8',
    borderRadius: ms(8),
    marginBottom: ms(16),
    borderWidth: ms(1),
    borderColor: '#E0E0E0',
    overflow: 'hidden',
  },
  relationshipOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: ms(12),
    paddingHorizontal: ms(14),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E8E8E8',
    backgroundColor: '#FFF',
  },
  relationshipOptionSelected: {
    backgroundColor: '#E8F8F8',
  },
  relationshipOptionText: {
    fontSize: ms(15, 0.3),
    color: '#333',
  },
  relationshipOptionTextSelected: {
    color: '#29BFC0',
    fontWeight: '600',
  },
  relationshipCheckmark: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    fontWeight: 'bold',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: ms(12),
  },
  modalButton: {
    flex: 1,
    paddingVertical: ms(14),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#F0F0F0',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
  },
  saveButtonText: {
    color: '#FFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
});

export default SecretCodeRecipientsScreen;
